// ==========================================================================
// lib/csv.ts — read a bank CSV in the browser (FF-010, 9 Oct 2026)
// Nothing leaves the browser except the parsed rows the user confirms.
// Handles quoted fields, commas inside quotes, CRLF, a header row or not,
// amounts like "-84.50", "$1,234.00", "(84.50)", separate debit/credit
// columns, and the common Australian bank date formats.
// ==========================================================================

export function parseCsv(text: string): string[][] {
  const rows: string[][] = []; let row: string[] = []; let cell = ""; let q = false;
  const t = text.replace(/^﻿/, "");
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (q) {
      if (c === '"' && t[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') q = false;
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && t[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else cell += c;
  }
  row.push(cell);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  return rows.map((r) => r.map((x) => x.trim()));
}

export type DateFormat = "d/m/y" | "y-m-d" | "m/d/y" | "d mon y";
export const DATE_FORMATS: { key: DateFormat; label: string; example: string }[] = [
  { key: "d/m/y", label: "Day/Month/Year", example: "09/10/2026" },
  { key: "y-m-d", label: "Year-Month-Day", example: "2026-10-09" },
  { key: "d mon y", label: "Day Mon Year", example: "09 Oct 2026" },
  { key: "m/d/y", label: "Month/Day/Year", example: "10/09/2026" },
];
const MON = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

/** Returns yyyy-MM-dd or null. */
export function parseDate(s: string, f: DateFormat): string | null {
  const v = s.trim(); let d = 0, m = 0, y = 0;
  if (f === "y-m-d") { const r = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); if (!r) return null; y = +r[1]; m = +r[2]; d = +r[3]; }
  else if (f === "d mon y") { const r = v.match(/^(\d{1,2})[\s-]([A-Za-z]{3})[A-Za-z]*[\s-](\d{2,4})/); if (!r) return null; d = +r[1]; m = MON.indexOf(r[2].toLowerCase()) + 1; y = +r[3]; }
  else { const r = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/); if (!r) return null; if (f === "d/m/y") { d = +r[1]; m = +r[2]; } else { m = +r[1]; d = +r[2]; } y = +r[3]; }
  if (y < 100) y += 2000;
  if (!m || m > 12 || !d || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** "-$1,234.50", "(84.50)", "84.50 DR" -> cents (signed), or null. */
export function parseAmount(s: string): number | null {
  let v = s.trim(); if (!v) return null;
  let neg = false;
  if (/^\(.*\)$/.test(v)) { neg = true; v = v.slice(1, -1); }
  if (/\bDR$/i.test(v)) { neg = true; v = v.replace(/\s*DR$/i, ""); }
  v = v.replace(/\s*CR$/i, "");
  if (v.startsWith("-")) { neg = !neg; v = v.slice(1); }
  v = v.replace(/[$,\s]/g, "");
  if (!/^\d*\.?\d+$/.test(v)) return null;
  const c = Math.round(Number(v) * 100);
  return neg ? -c : c;
}

export type Mapping = {
  hasHeader: boolean; date: number; description: number;
  amount: number | null; debit: number | null; credit: number | null; balance: number | null;
  dateFormat: DateFormat; flipSign: boolean;
};

/** Guess a mapping from the header row. */
export function guessMapping(rows: string[][]): Mapping {
  const head = (rows[0] ?? []).map((h) => h.toLowerCase());
  const hasHeader = head.some((h) => /date|amount|description|narrative|details|debit|credit|balance/.test(h));
  const find = (re: RegExp) => { const i = head.findIndex((h) => re.test(h)); return i === -1 ? null : i; };
  const date = (hasHeader ? find(/date/) : null) ?? 0;
  const description = (hasHeader ? find(/description|narrative|details|transaction details|particulars|merchant/) : null) ?? 2;
  const debit = hasHeader ? find(/debit|withdraw/) : null;
  const credit = hasHeader ? find(/credit|deposit/) : null;
  // A single signed amount column, unless the file splits debits and credits.
  const amount = !hasHeader ? 1 : debit !== null || credit !== null ? find(/^amount$/) : find(/amount/);
  const balance = hasHeader ? find(/balance/) : null;
  const sample = rows.slice(hasHeader ? 1 : 0, hasHeader ? 6 : 5).map((r) => r[date] ?? "");
  const fmt = (DATE_FORMATS.map((f) => f.key) as DateFormat[]).find((f) => sample.length > 0 && sample.every((s) => parseDate(s, f))) ?? "d/m/y";
  return { hasHeader, date, description, amount: amount ?? (debit === null && credit === null ? 1 : null), debit, credit, balance, dateFormat: fmt, flipSign: false };
}

export type ParsedRow = { posted_on: string; amount_cents: number; description: string; balance_cents: number | null };
export type ParseResult = { rows: ParsedRow[]; bad: { line: number; reason: string }[] };

export function applyMapping(rows: string[][], m: Mapping): ParseResult {
  const out: ParsedRow[] = []; const bad: { line: number; reason: string }[] = [];
  rows.slice(m.hasHeader ? 1 : 0).forEach((r, i) => {
    const line = i + (m.hasHeader ? 2 : 1);
    const date = parseDate(r[m.date] ?? "", m.dateFormat);
    if (!date) { bad.push({ line, reason: `date "${r[m.date] ?? ""}" not understood` }); return; }
    let amt: number | null = null;
    if (m.amount !== null) amt = parseAmount(r[m.amount] ?? "");
    else {
      const d = m.debit !== null ? parseAmount(r[m.debit] ?? "") : null;
      const c = m.credit !== null ? parseAmount(r[m.credit] ?? "") : null;
      if (d) amt = -Math.abs(d); else if (c) amt = Math.abs(c);
    }
    if (amt === null || amt === 0) { bad.push({ line, reason: "no amount" }); return; }
    if (m.flipSign) amt = -amt;
    const description = (r[m.description] ?? "").replace(/\s+/g, " ").trim() || "(no description)";
    const balance_cents = m.balance !== null ? parseAmount(r[m.balance] ?? "") : null;
    out.push({ posted_on: date, amount_cents: amt, description, balance_cents });
  });
  return { rows: out, bad };
}

export async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Fingerprint per row: date | amount | description (lower-cased, spaces
 * squashed) | n, where n counts identical rows earlier in the same file.
 * Re-importing an overlapping export gives the same fingerprints, so rows
 * already in the app are skipped; two genuinely identical purchases on the
 * same day stay as two rows.
 */
export async function fingerprints(rows: ParsedRow[]): Promise<string[]> {
  const seen = new Map<string, number>();
  const out: string[] = [];
  for (const r of rows) {
    const key = `${r.posted_on}|${r.amount_cents}|${r.description.toLowerCase().replace(/\s+/g, " ")}`;
    const n = seen.get(key) ?? 0; seen.set(key, n + 1);
    out.push(await sha256(`${key}|${n}`));
  }
  return out;
}
